import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';

import { useStyles } from '../GraderPage.style.js';

import { GraderList } from './GraderList.js';

const MIN_WIDTH = 240;
const MAX_WIDTH = 600;
const clampWidth = (value: number) =>
  Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, window.innerWidth - 400, value));

export function GraderListRail() {
  const styles = useStyles();
  const [width, setWidth] = useState(() => clampWidth(340));
  const drag = useRef<{ x: number; width: number } | null>(null);

  useEffect(() => {
    const onResize = () => setWidth((current) => clampWidth(current));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    drag.current = { x: event.clientX, width };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current) {
      setWidth(clampWidth(drag.current.width + event.clientX - drag.current.x));
    }
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const delta =
      event.key === 'ArrowLeft' ? -24 : event.key === 'ArrowRight' ? 24 : 0;
    if (delta || event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      setWidth(
        clampWidth(
          event.key === 'Home'
            ? MIN_WIDTH
            : event.key === 'End'
              ? MAX_WIDTH
              : width + delta,
        ),
      );
    }
  };

  return (
    <div className={styles.listRail} style={{ width }}>
      <GraderList />
      <div
        className={styles.listRailHandle}
        role="slider"
        aria-label="Resize judge list"
        aria-orientation="horizontal"
        aria-valuemin={MIN_WIDTH}
        aria-valuemax={clampWidth(MAX_WIDTH)}
        aria-valuenow={width}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
      />
    </div>
  );
}
